import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Sparkles,
  CheckCircle2,
  ArrowUpCircle,
  Crown,
  CreditCard,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { StatusPill } from "@/components/uiext";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

function gauge(used, max) {
  if (!max) return { pct: 0, kind: "info", label: `${used} / ∞` };
  const pct = Math.round((used / max) * 100);
  const kind = pct >= 90 ? "danger" : pct >= 70 ? "warn" : "ok";
  return { pct: Math.min(100, pct), kind, label: `${used} / ${max}` };
}

function UsageRow({ label, used, max, testid }) {
  const g = gauge(used, max);
  return (
    <div data-testid={testid} className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono">{g.label}</span>
      </div>
      <Progress
        value={g.pct}
        className={`h-1.5 ${
          g.kind === "danger"
            ? "[&>div]:bg-[hsl(var(--danger))]"
            : g.kind === "warn"
            ? "[&>div]:bg-[hsl(var(--warn))]"
            : "[&>div]:bg-[hsl(var(--ok))]"
        }`}
      />
    </div>
  );
}

/**
 * Tenant-side "My Plan" card. Drop into Billing page.
 * Shows current tier, live usage gauges, and an "Upgrade" CTA.
 */
export function MyPlanCard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [requesting, setRequesting] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // Phase E3 — Stripe self-serve subscription state
  const [stripeCfg, setStripeCfg] = useState({ enabled: false });
  const [stripeBusy, setStripeBusy] = useState(false);

  const load = async () => {
    try {
      const [r, p, s] = await Promise.all([
        api.get("/billing/my-plan"),
        api.get("/billing/plans"),
        api.get("/billing/stripe-config").catch(() => ({ data: { enabled: false } })),
      ]);
      setData(r.data);
      setAllPlans(p.data.plans || []);
      setStripeCfg(s.data || { enabled: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (!data) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          {t("common.loading", "Loading…")}
        </CardContent>
      </Card>
    );
  }

  const plan = data.plan;
  const u = data.usage || {};
  const lim = plan?.limits || {};

  const submitUpgrade = async () => {
    if (!requesting) return;
    setBusy(true);
    try {
      await api.post("/billing/upgrade-request", {
        plan_slug: requesting.slug,
        note,
      });
      toast.success(
        t("plans.upgradeRequested", "Upgrade request sent — we'll be in touch.")
      );
      setRequesting(null);
      setNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  // Phase E3 — Self-serve Stripe Checkout. Only fires when the platform
  // has Stripe configured AND the chosen plan has a stripe_price_id set.
  const subscribeStripe = async (plan) => {
    if (!plan?.slug) return;
    setStripeBusy(true);
    try {
      const r = await api.post("/billing/checkout", {
        plan_slug: plan.slug,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        // Hand the user off to Stripe-hosted Checkout. The success URL
        // brings them back to /billing/checkout/return where we poll
        // for the final payment status.
        window.location.assign(r.data.url);
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setStripeBusy(false);
    }
  };

  const planHasStripe = (plan) =>
    Boolean(plan && stripeCfg.enabled && plan.stripe_price_id);

  // Phase H — open the Stripe-hosted Customer Portal so paid tenants can
  // self-manage payment method / cancel without contacting support.
  const openCustomerPortal = async () => {
    setStripeBusy(true);
    try {
      const r = await api.post("/billing/portal", {
        origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.assign(r.data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open portal");
      setStripeBusy(false);
    }
  };

  // Tenants are "subscribed via Stripe" once the org doc has been marked
  // by the webhook / status poll. The flag is on `data` not `plan`.
  const subscribedViaStripe = Boolean(
    data?.org?.stripe_subscription_active && stripeCfg.enabled
  );

  return (
    <>
      <Card data-testid="my-plan-card">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                {plan?.features?.white_label ? (
                  <Crown className="h-4 w-4 text-[hsl(var(--accent))]" />
                ) : (
                  <Sparkles className="h-4 w-4 text-[hsl(var(--accent))]" />
                )}
                <h3 className="text-base font-semibold">
                  {plan?.name || t("plans.noPlan", "No plan assigned")}
                </h3>
                {plan && (
                  <StatusPill kind="info">
                    {plan.billing_metric === "per_extension"
                      ? `$${plan.price_per_unit_usd.toFixed(2)}/ext`
                      : plan.billing_metric === "per_concurrent_call"
                      ? `$${plan.price_per_unit_usd.toFixed(2)}/cc`
                      : `$${plan.price_monthly_usd.toFixed(0)}/mo`}
                  </StatusPill>
                )}
              </div>
              {plan?.description && (
                <p className="text-xs text-muted-foreground mt-1">
                  {plan.description}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {subscribedViaStripe && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openCustomerPortal}
                  disabled={stripeBusy}
                  data-testid="open-customer-portal"
                  className="gap-1"
                >
                  <CreditCard className="h-3.5 w-3.5" />
                  {stripeBusy
                    ? t("plans.opening", "Opening…")
                    : t("plans.manageSubscription", "Manage subscription")}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCompare(true)}
                data-testid="compare-plans-btn"
              >
                <ArrowUpCircle className="h-3.5 w-3.5 mr-1" />
                {t("plans.compare", "Compare plans")}
              </Button>
            </div>
          </div>

          {data.charge && plan?.billing_metric !== "flat" && (
            <div
              className="rounded-md border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5 px-3 py-2"
              data-testid="charge-breakdown"
            >
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {t("plans.thisPeriod", "This period (current usage)")}
                </span>
                <span className="text-2xl font-bold tabular-nums">
                  ${data.charge.total.toFixed(2)}
                  <span className="text-xs text-muted-foreground font-normal ml-1">
                    /mo
                  </span>
                </span>
              </div>
              <div className="text-[11px] font-mono text-muted-foreground mt-0.5">
                {data.charge.qty} {data.charge.qty_unit}
                {data.charge.qty !== 1 ? "s" : ""} × $
                {data.charge.effective_rate.toFixed(2)}
                {data.charge.volume_discount_percent > 0 && (
                  <span className="text-[hsl(var(--ok))]">
                    {" "}
                    (−{data.charge.volume_discount_percent.toFixed(0)}% volume @{" "}
                    {data.charge.volume_discount_threshold}+)
                  </span>
                )}{" "}
                = ${data.charge.subtotal.toFixed(2)}
                {data.charge.min_floor_applied && (
                  <span className="text-[hsl(var(--warn))]">
                    {" "}
                    → ${data.charge.min_monthly.toFixed(2)} min applied
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <UsageRow
              label={t("plans.usage.cc", "Concurrent calls (live)")}
              used={u.concurrent_calls || 0}
              max={lim.concurrent_calls || 0}
              testid="usage-cc"
            />
            <UsageRow
              label={t("plans.usage.users", "SIP users")}
              used={u.users || 0}
              max={lim.max_users || 0}
              testid="usage-users"
            />
            <UsageRow
              label={t("plans.usage.trunks", "Trunks")}
              used={u.trunks || 0}
              max={lim.max_trunks || 0}
              testid="usage-trunks"
            />
            <UsageRow
              label={t("plans.usage.domains", "Domains")}
              used={u.domains || 0}
              max={lim.max_domains || 0}
              testid="usage-domains"
            />
          </div>

          {plan?.features && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {Object.entries(plan.features)
                .filter(([, v]) => v)
                .map(([k]) => (
                  <span
                    key={k}
                    className="text-[10px] px-1.5 py-0.5 rounded border border-[hsl(var(--ok))]/40 bg-[hsl(var(--ok))]/10 text-[hsl(var(--ok))] inline-flex items-center gap-1"
                  >
                    <CheckCircle2 className="h-2.5 w-2.5" />
                    {t(`plans.feature.${k}`, k.replace(/_/g, " "))}
                  </span>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCompare} onOpenChange={setShowCompare}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t("plans.compareTitle", "Plan comparison")}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {allPlans.map((p) => {
              const isCurrent = plan?.slug === p.slug;
              return (
                <Card
                  key={p.id}
                  className={`${
                    isCurrent
                      ? "border-[hsl(var(--accent))] ring-1 ring-[hsl(var(--accent))]/30"
                      : ""
                  }`}
                  data-testid={`compare-plan-${p.slug}`}
                >
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm">{p.name}</h4>
                      {isCurrent && (
                        <StatusPill kind="ok">{t("plans.current", "current")}</StatusPill>
                      )}
                    </div>
                    <div className="text-2xl font-bold">
                      {p.billing_metric === "per_extension" ? (
                        <>
                          ${p.price_per_unit_usd.toFixed(2)}
                          <span className="text-xs text-muted-foreground font-normal ml-1">
                            /ext/mo
                          </span>
                        </>
                      ) : p.billing_metric === "per_concurrent_call" ? (
                        <>
                          ${p.price_per_unit_usd.toFixed(2)}
                          <span className="text-xs text-muted-foreground font-normal ml-1">
                            /cc/mo
                          </span>
                        </>
                      ) : (
                        <>
                          ${p.price_monthly_usd.toFixed(0)}
                          <span className="text-xs text-muted-foreground font-normal ml-1">
                            /mo
                          </span>
                        </>
                      )}
                    </div>
                    {p.min_monthly_usd > 0 && p.billing_metric !== "flat" && (
                      <p className="text-[10px] text-muted-foreground">
                        ${p.min_monthly_usd.toFixed(0)}/mo {t("plans.minimum", "minimum")}
                      </p>
                    )}
                    <ul className="text-[11px] space-y-1 text-muted-foreground">
                      <li>
                        <span className="font-mono text-foreground">
                          {p.limits.concurrent_calls}
                        </span>{" "}
                        {t("plans.usage.cc", "concurrent calls")}
                      </li>
                      <li>
                        <span className="font-mono text-foreground">
                          {p.limits.max_users || "∞"}
                        </span>{" "}
                        {t("plans.usage.users", "users")}
                      </li>
                      <li>
                        <span className="font-mono text-foreground">
                          {p.limits.max_trunks || "∞"}
                        </span>{" "}
                        {t("plans.usage.trunks", "trunks")}
                      </li>
                      <li>
                        {p.limits.cdr_retention_days
                          ? `${p.limits.cdr_retention_days}d CDR`
                          : "∞ CDR"}
                      </li>
                    </ul>
                    <div className="space-y-0.5 text-[10px] pt-1 border-t border-border">
                      {Object.entries(p.features).map(([k, v]) => (
                        <div
                          key={k}
                          className={`flex items-center gap-1 ${v ? "text-[hsl(var(--ok))]" : "text-muted-foreground/40"}`}
                        >
                          <CheckCircle2 className="h-2.5 w-2.5" />
                          <span>{t(`plans.feature.${k}`, k.replace(/_/g, " "))}</span>
                        </div>
                      ))}
                    </div>
                    {!isCurrent && (
                      planHasStripe(p) ? (
                        <Button
                          size="sm"
                          className="w-full gap-1"
                          variant={p.sort_order > (plan?.sort_order || 0) ? "default" : "outline"}
                          onClick={() => subscribeStripe(p)}
                          disabled={stripeBusy}
                          data-testid={`stripe-subscribe-${p.slug}`}
                        >
                          <CreditCard className="h-3.5 w-3.5" />
                          {stripeBusy
                            ? t("plans.redirecting", "Redirecting…")
                            : t("plans.subscribeStripe", "Subscribe")}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          className="w-full"
                          variant={p.sort_order > (plan?.sort_order || 0) ? "default" : "outline"}
                          onClick={() => setRequesting(p)}
                          data-testid={`request-upgrade-${p.slug}`}
                        >
                          {p.sort_order > (plan?.sort_order || 0)
                            ? t("plans.upgrade", "Upgrade")
                            : t("plans.switch", "Switch")}
                        </Button>
                      )
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!requesting} onOpenChange={(o) => !o && setRequesting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("plans.upgradeReqTitle", "Request plan change")}: {requesting?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            {t(
              "plans.upgradeReqHint",
              "We'll review your request and get back to you. No charge yet — billing is invoiced separately."
            )}
          </p>
          <div>
            <Label className="text-xs">
              {t("plans.upgradeReqNote", "Note (optional)")}
            </Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t(
                "plans.upgradeReqPh",
                "Anything we should know? Effective date, custom requirements, etc."
              )}
              data-testid="upgrade-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequesting(null)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button onClick={submitUpgrade} disabled={busy} data-testid="submit-upgrade">
              {busy ? t("common.loading", "…") : t("plans.sendRequest", "Send request")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MyPlanCard;
